import BorderColorIcon from '@mui/icons-material/BorderColor';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import { BooleanInput, Form, TextInput, useNotify, usePermissions, useTranslate, useUpdate } from 'react-admin';
import LoadingButton from '@mui/lab/LoadingButton';
import useGetAvatar from '@/hooks/useGetAvatar';
import { composeUri } from '@/utils/compose';
import {
    AVATARS,
    PLACES,
    ROLE_BIG_DEMO,
} from '@/utils/CONST';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import FormStateConsumer from '@/components/common/form-state-consumer/form-state-consumer';

interface EditPlaceDialogProps {
    open: boolean;
    onClose: () => void;
    placeId: string;
    siteId: string;
}

const EditPlaceDialog = ({ open, onClose, placeId, siteId }: EditPlaceDialogProps) => {
    const translate = useTranslate();
    const notify = useNotify();
    const { permissions } = usePermissions();
    const [update, { isLoading: isLoadingUpdate }] = useUpdate();
    const { avatar, isLoading: isLoadingAvatar } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, placeId ?? ''),
        queryKey: `${PLACES}_${siteId}`,
    });

    const handleSubmit = ({ name, demo }: any) => {
        update(
            `${PLACES}_${siteId}`,
            {
                id: avatar.id,
                data: {
                    name,
                    customFields: {
                        demo: !!demo,
                    },
                },
                meta: {
                    realResource: AVATARS,
                },
            },
            {
                onSuccess: () => {
                    notify('ra.notification.updated', {
                        type: 'success',
                        messageArgs: {
                            smart_count: 1,
                        },
                    });
                    onClose();
                },
            },
        );
    };

    return (
        <DialogTemplate
            icon={<BorderColorIcon />}
            title={translate('place.edit_place_label', {
                placeName: avatar?.name,
            })}
            open={open}
            onClose={onClose}
        >
            <Form
                onSubmit={handleSubmit}
                defaultValues={{
                    name: avatar?.name,
                    demo: avatar?.customFields?.demo ?? false,
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        <TextInput helperText={false} name={'name'} source={'name'} label={'place.form.place_name'} />
                        {permissions?.includes(ROLE_BIG_DEMO) && (
                            <BooleanInput
                                label='place.form.demo'
                                source='demo'
                                helperText={false}
                                sx={{ ml: 1 }}
                            />
                        )}
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button onClick={onClose} disabled={isLoadingUpdate}>
                            {translate('buttons.cancel')}
                        </Button>

                        <FormStateConsumer>
                            {({ isDirty }: any) => (
                                <LoadingButton
                                    type='submit'
                                    variant='contained'
                                    loading={isLoadingUpdate || isLoadingAvatar}
                                    disabled={!isDirty}
                                >
                                    {translate('buttons.validate')}
                                </LoadingButton>
                            )}
                        </FormStateConsumer>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default EditPlaceDialog;

